import type { BoardResponse, ColumnInfo, ItemInfo } from '../api/client'
import { labelTable } from './BoardCell'

/** CSV 单元格转义：含逗号 / 引号 / 换行时用双引号包住，内部引号翻倍 */
function cell(raw: string): string {
  const s = raw ?? ''
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`
  return s
}

/** 标签列显示标签文本，其它列显示原值 */
function display(item: ItemInfo, col: ColumnInfo): string {
  const raw = col.key === 'name' ? item.name : (item.values[col.key] ?? '')
  if (raw === '') return ''
  if (col.type === 'color' || col.type === 'dropdown') {
    const t = labelTable(col)
    const text = t.text[raw]
    if (text !== undefined && text !== '') return text
  }
  return raw
}

/**
 * 导出为 CSV（M8）。
 *
 * 结构：每个分组一段 —— 先一行分组标题，再一行列头，然后逐条目一行；段与段之间空一行。
 * 列顺序按 pos；标签类列输出标签文本而不是索引，方便在 Excel 里看。
 */
export function boardToCsv(data: BoardResponse): string {
  const cols = [...data.columns]
    .filter((c) => c.key !== 'name')
    .sort((a, b) => a.pos - b.pos)
  const groups = [...data.groups].sort((a, b) => a.pos - b.pos)
  const lines: string[] = []

  for (const g of groups) {
    const items = data.items
      .filter((it) => it.groupId === g.id)
      .sort((a, b) => a.pos - b.pos)
    lines.push(cell(g.title))
    lines.push(['名称', ...cols.map((c) => c.title)].map(cell).join(','))
    for (const it of items) {
      lines.push([it.name, ...cols.map((c) => display(it, c))].map(cell).join(','))
    }
    lines.push('')
  }

  // 末尾空行去掉，行尾统一 CRLF（Excel 最稳）
  while (lines.length > 0 && lines[lines.length - 1] === '') lines.pop()
  return lines.join('\r\n')
}

/** 导出为 JSON：完整看板结构（board / columns / groups / items） */
export function boardToJson(data: BoardResponse): string {
  return JSON.stringify(
    {
      version: 1,
      exportedAt: new Date().toISOString(),
      board: data.board,
      columns: data.columns,
      groups: data.groups,
      items: data.items,
    },
    null,
    2,
  )
}

/**
 * 触发浏览器下载（纯前端，Blob + a[download]）。
 *
 * CSV 要加 UTF-8 BOM：Excel 打开无 BOM 的 UTF-8 文件时会按系统 ANSI（简中为 GBK）
 * 解析，中文就成乱码。JSON 不能加 BOM —— 严格的 JSON 解析器会因此报错。
 */
export function downloadText(fileName: string, mime: string, text: string): void {
  const body = mime.startsWith('text/csv') ? `\uFEFF${text}` : text
  const blob = new Blob([body], { type: `${mime};charset=utf-8` })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  a.remove()
  // 交给浏览器接住后再释放
  setTimeout(() => URL.revokeObjectURL(url), 0)
}

/** 文件名里去掉 Windows 不允许的字符 */
export function safeFileName(name: string): string {
  return name.replace(/[\\/:*?"<>|]/g, '_').trim() || 'board'
}
